import { useEffect, useState } from 'react';
import type { CreateReviewPayload, MgmtReviewInputs } from './types';
import { fetchInputsPreview } from './managementReviewApi';
import { InputsSummaryCharts } from './InputsSummaryCharts';
import { formatPeriodLabel } from './managementReviewUtils';

interface ReviewFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateReviewPayload) => Promise<void>;
}

function defaultPeriodDates() {
  const end = new Date();
  const start = new Date(end.getFullYear(), 0, 1);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd: end.toISOString().slice(0, 10),
    reviewDate: end.toISOString().slice(0, 10),
  };
}

export function ReviewFormModal({ open, onClose, onSubmit }: ReviewFormModalProps) {
  const defaults = defaultPeriodDates();
  const [title, setTitle] = useState('');
  const [reviewDate, setReviewDate] = useState(defaults.reviewDate);
  const [periodStart, setPeriodStart] = useState(defaults.periodStart);
  const [periodEnd, setPeriodEnd] = useState(defaults.periodEnd);
  const [preview, setPreview] = useState<MgmtReviewInputs | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setError(null);
    setPreview(null);
  }, [open]);

  const loadPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const data = await fetchInputsPreview(
        new Date(periodStart).toISOString(),
        new Date(periodEnd).toISOString(),
      );
      setPreview(data);
    } catch {
      setError('No se pudo cargar el preview de entradas.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        title,
        reviewDate: new Date(reviewDate).toISOString(),
        periodStart: new Date(periodStart).toISOString(),
        periodEnd: new Date(periodEnd).toISOString(),
      });
      onClose();
    } catch {
      setError('No se pudo crear la revisión.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <h2 className="text-xl font-semibold">Nueva revisión por la dirección</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              Título
              <input
                className="input mt-1 w-full"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={255}
                placeholder="Ej: Revisión anual 2026"
              />
            </label>
            <label className="block text-sm">
              Fecha de revisión
              <input
                type="date"
                className="input mt-1 w-full"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                required
              />
            </label>
            <label className="block text-sm">
              Inicio del período
              <input
                type="date"
                className="input mt-1 w-full"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </label>
            <label className="block text-sm">
              Fin del período
              <input
                type="date"
                className="input mt-1 w-full"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void loadPreview()}
              disabled={loadingPreview}
            >
              {loadingPreview ? 'Cargando preview…' : 'Ver entradas del período'}
            </button>
            {preview && (
              <span className="text-sm text-muted-foreground">
                Período: {formatPeriodLabel(periodStart, periodEnd)}
              </span>
            )}
          </div>

          {preview && <InputsSummaryCharts inputs={preview} />}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !title.trim()}>
              {submitting ? 'Creando…' : 'Crear revisión'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
